import React, { useEffect, useRef } from 'react';

import Loading from 'components/common/loading/Loading';
import { handleError } from 'utils/errorHandler';

import { useRolesQuery } from 'hooks/query/useRoles';

import { RolesPermissionsTable } from './components';
import { usePermissionsQuery, useRolePermissionsQuery } from 'hooks/query/useRolesPermissions';

export type RoleItem = {
  id: number;
  name: string;
};

export type PermissionItem = {
  id: number;
  label: string;
};

export type RolePermissionRecord = {
  id: number;
  permissions: PermissionItem[];
};

const RolesPermissions: React.FC = () => {
  const {
    data: roles = [],
    isLoading: isRolesLoading,
    isError: isRolesError,
    error: rolesError,
    errorUpdatedAt: rolesErrorUpdatedAt,
  } = useRolesQuery();
  const {
    data: permissions = [],
    isLoading: isPermissionsLoading,
    isError: isPermissionsError,
    error: permissionsError,
    errorUpdatedAt: permissionsErrorUpdatedAt,
  } = usePermissionsQuery();
  const {
    data: rolesPermissions = [],
    isLoading: isRolesPermissionsLoading,
    isError: isRolesPermissionsError,
    error: rolesPermissionsError,
    errorUpdatedAt: rolesPermissionsErrorUpdatedAt,
  } = useRolePermissionsQuery();

  const lastHandledRolesErrorAtRef = useRef(0);
  const lastHandledPermissionsErrorAtRef = useRef(0);
  const lastHandledRolesPermissionsErrorAtRef = useRef(0);

  useEffect(() => {
    window.document.title = 'Roles Permissions | Vyaguta';
  }, []);

  useEffect(() => {
    if (!isRolesError || !rolesError || rolesErrorUpdatedAt <= lastHandledRolesErrorAtRef.current) {
      return;
    }

    handleError(rolesError);
    lastHandledRolesErrorAtRef.current = rolesErrorUpdatedAt;
  }, [isRolesError, rolesError, rolesErrorUpdatedAt]);

  useEffect(() => {
    if (
      !isPermissionsError ||
      !permissionsError ||
      permissionsErrorUpdatedAt <= lastHandledPermissionsErrorAtRef.current
    ) {
      return;
    }

    handleError(permissionsError);
    lastHandledPermissionsErrorAtRef.current = permissionsErrorUpdatedAt;
  }, [isPermissionsError, permissionsError, permissionsErrorUpdatedAt]);

  useEffect(() => {
    if (
      !isRolesPermissionsError ||
      !rolesPermissionsError ||
      rolesPermissionsErrorUpdatedAt <= lastHandledRolesPermissionsErrorAtRef.current
    ) {
      return;
    }

    handleError(rolesPermissionsError);
    lastHandledRolesPermissionsErrorAtRef.current = rolesPermissionsErrorUpdatedAt;
  }, [isRolesPermissionsError, rolesPermissionsError, rolesPermissionsErrorUpdatedAt]);

  return (
    <main>
      <div className="container">
        <div className="full-scope-card">
          <div className="full-scope-card__header table-header name-wrap">
            <div className="d-flex flex-row">
              <h3 className="table-title">Roles Permissions</h3>
            </div>
          </div>

          <div className="full-scope-card__content">
            <div className="roles-permissions-wrapper">
              {isPermissionsLoading || isRolesLoading || isRolesPermissionsLoading ? (
                <Loading className="mt-25" />
              ) : (
                <RolesPermissionsTable permissions={permissions} roles={roles} rolesPermissions={rolesPermissions} />
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default RolesPermissions;
